/**
 * Thẻ ưu đãi — sáu slot cố định, một khuôn duy nhất cho mọi loại ưu đãi.
 *
 * Component này KHÔNG đọc field `scheme` và không rẽ nhánh theo loại ưu đãi.
 * Back office có khái niệm scheme (combo giờ chơi, tặng kèm, giảm giá, giá
 * khung giờ) nhưng đó là chuyện của back office: thêm scheme mới không được
 * kéo theo một lần sửa front office. Ở đây chỉ có sáu slot, đổ dữ liệu đã tính
 * sẵn vào.
 *
 *   1. Huy hiệu        code + ribbon
 *   2. Dải tiết kiệm   pricing.savingAmount  (null hoặc <= 0 thì ẩn cả dải)
 *   3. Quyền lợi       benefits              (1 dòng thì là câu lớn, không bullet)
 *   4. Đối tượng       eligibility.label
 *   5. Khung giờ       schedule
 *   6. Giá             pricing
 *
 * Khối giá dính đáy thẻ bằng margin-top:auto, nên thẻ 1 quyền lợi và thẻ 4
 * quyền lợi đứng cạnh nhau vẫn thẳng giá với nhau.
 *
 * Cả thẻ là một link tel: — bỏ luồng đặt bàn rồi thì gọi hotline là cách duy
 * nhất để dùng ưu đãi này.
 */
import React from "react";
import TIcon from "../../components/ui/TIcon";
import { Promotion } from "../../interfaces/venue";
import { formatSchedule, formatVnd, hasSaving, telHref } from "../../helpers/venue";

interface Props {
  promotion: Promotion;
  hotline: string;
}

const PromotionCard: React.FC<Props> = ({ promotion, hotline }) => {
  const single = promotion.benefits.length === 1;

  return (
    <a
      className={`vn-promo${promotion.display.isFeatured ? " featured" : ""}`}
      href={telHref(hotline)}
      aria-label={`Ưu đãi ${promotion.code} — gọi ${hotline} để đặt bàn`}
    >
      {/* Slot 1 — huy hiệu */}
      <div className="vn-promo-badges">
        <span className="vn-badge">Combo {promotion.code}</span>
        {promotion.ribbon ? (
          <span className="vn-badge ghost">{promotion.ribbon}</span>
        ) : null}
      </div>

      {/* Slot 2 — dải tiết kiệm */}
      {hasSaving(promotion) ? (
        <div className="vn-saving">
          Tiết kiệm {formatVnd(promotion.pricing.savingAmount as number)}
        </div>
      ) : null}

      {/* Slot 3 — quyền lợi */}
      {single ? (
        <div className="vn-benefit-single">{promotion.benefits[0]}</div>
      ) : (
        <ul className="vn-benefits">
          {promotion.benefits.map((benefit) => (
            <li key={benefit}>{benefit}</li>
          ))}
        </ul>
      )}

      {/* Slot 4 + 5 — đối tượng và khung giờ */}
      <div className="vn-promo-terms">
        <div className="vn-term">
          <TIcon name="users" />
          <span>{promotion.eligibility.label}</span>
        </div>
        <div className="vn-term">
          <TIcon name="clock-hour-4" />
          <span>{formatSchedule(promotion.schedule)}</span>
        </div>
      </div>

      {/* Slot 6 — giá */}
      <div className="vn-promo-price">
        {promotion.pricing.listPrice !== null ? (
          <span className="vn-price-old">
            <span className="sr-only">Giá gốc </span>
            {formatVnd(promotion.pricing.listPrice)}
          </span>
        ) : null}
        {promotion.pricing.isFree ? (
          <span className="vn-price-free">Miễn phí</span>
        ) : promotion.pricing.finalPrice === null ? (
          /* Chưa có giá thì nói "Liên hệ". formatVnd(0) ra "0đ" - đọc thành quán cho
             không, mà thật ra là admin chưa điền. Cùng luật với bảng giá theo giờ. */
          <span className="vn-price-final">Liên hệ</span>
        ) : (
          <span className="vn-price-final">
            {formatVnd(promotion.pricing.finalPrice)}
          </span>
        )}
      </div>
    </a>
  );
};

export default PromotionCard;
